import React, { useState } from 'react';
import '../styles/Requests.css';

function formatDate(value) {
  if (!value) return null;
  // Firestore Timestamp object
  if (value?.toDate) return value.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  // Firestore Timestamp as plain object with seconds
  if (value?.seconds) return new Date(value.seconds * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  // ISO string or other
  const d = new Date(value);
  if (!isNaN(d)) return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  return null;
}

function RefreshIcon({ spinning }) {
  return (
    <svg
      className={'refresh-icon' + (spinning ? ' spinning' : '')}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 12a9 9 0 0 1 15.5-6.36L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-15.5 6.36L3 16" />
      <path d="M3 21v-5h5" />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg
      className="btn-spinner"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M12 2a10 10 0 0 1 10 10" />
    </svg>
  );
}

function EmployeeRequestsSection({
  pendingUsers,
  sortedTeams,
  handleApproveUser,
  handleRejectUser,
  onRefresh,
}) {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [approvingIds, setApprovingIds] = useState(new Set());

  const MIN_SPIN_MS = 600;

  const handleRefreshClick = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    const startedAt = Date.now();
    try {
      if (onRefresh) {
        await onRefresh();
      } else {
        console.warn('EmployeeRequestsSection: no onRefresh handler was passed in — wire one up in the parent so this button actually refetches pending requests.');
      }
    } catch (err) {
      console.error('Failed to refresh employee requests:', err);
    } finally {
      const remaining = MIN_SPIN_MS - (Date.now() - startedAt);
      if (remaining > 0) {
        await new Promise(resolve => setTimeout(resolve, remaining));
      }
      setIsRefreshing(false);
    }
  };

  const handleApproveClick = async (userId) => {
    const teamId = document.getElementById(`team-select-${userId}`).value;
    setApprovingIds(prev => new Set(prev).add(userId));
    try {
      await handleApproveUser(userId, teamId);
    } finally {
      setApprovingIds(prev => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
  };

  return (
    <>
      <div className="dashboard-header">
        <div className="requests-header-row">
          <div>
            <h1>Employee Requests</h1>
            <p>Approve employees and assign them to teams</p>
          </div>
          <button
            type="button"
            className="btn-refresh"
            onClick={handleRefreshClick}
            disabled={isRefreshing}
          >
            <RefreshIcon spinning={isRefreshing} />
            {isRefreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="requests-list">
        {pendingUsers.length === 0 ? (
          <div className="empty-state">
            <p>No pending requests</p>
          </div>
        ) : (
          pendingUsers.map(user => {
            const requestedDate = formatDate(user.createdAt) || formatDate(user.updatedAt);
            const isApproving = approvingIds.has(user.id);
            return (
              <div key={user.id} className={`request-card${isApproving ? ' request-card--approving' : ''}`}>
                <div className="request-info">
                  <h3>{user.name || 'No name provided'}</h3>
                  <p>{user.email}</p>
                  {requestedDate && (
                    <p className="request-date">Requested: {requestedDate}</p>
                  )}
                </div>
                <div className="request-actions">
                  <select
                    className="team-select"
                    defaultValue=""
                    id={`team-select-${user.id}`}
                    disabled={isApproving}
                  >
                    <option value="" disabled>Select Team</option>
                    {sortedTeams.map(team => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                  <button
                    className="btn-approve"
                    onClick={() => handleApproveClick(user.id)}
                    disabled={isApproving}
                  >
                    {isApproving ? (
                      <>
                        <SpinnerIcon />
                        Approving…
                      </>
                    ) : (
                      'Approve'
                    )}
                  </button>
                  <button
                    className="btn-reject"
                    onClick={() => handleRejectUser(user.id)}
                    disabled={isApproving}
                  >
                    Reject
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );
}

export default EmployeeRequestsSection;